import { ChatWebSocketError } from './errors';

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed';

export interface WebSocketClientConfig {
  url: string;
}

export interface WebSocketClient {
  connect(): void;
  disconnect(): void;
  send(payload: unknown): void;
  getState(): ConnectionState;
  onMessage(handler: (payload: unknown) => void): void;
  offMessage(handler: (payload: unknown) => void): void;
  onStateChange(handler: (state: ConnectionState) => void): void;
  offStateChange(handler: (state: ConnectionState) => void): void;
}

// Exponential backoff for automatic reconnection after an unexpected drop
// (network blip, idle proxy timeout, brief server restart) — capped so a
// long outage still retries at a steady, low-frequency interval rather than
// escalating forever or hammering the server.
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30_000;

export function createWebSocketClient(config: WebSocketClientConfig): WebSocketClient {
  let socket: WebSocket | null = null;
  let state: ConnectionState = 'idle';
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Only an explicit disconnect() sets this — it's what distinguishes "the
  // caller tore this down on purpose" (never reconnect) from "the socket
  // closed on its own" (reconnect automatically) in the shared 'close'
  // handler below.
  let disconnectedByCaller = false;
  const messageHandlers = new Set<(payload: unknown) => void>();
  const stateHandlers = new Set<(state: ConnectionState) => void>();

  function setState(next: ConnectionState): void {
    // Disconnecting sets state synchronously, but the underlying socket's own
    // close event fires afterward regardless and would otherwise re-report
    // the same transition a second time. Skipping a no-op transition keeps
    // each logical state change emitted exactly once.
    if (next === state) {
      return;
    }

    state = next;

    for (const handler of stateHandlers) {
      handler(next);
    }
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function scheduleReconnect(): void {
    if (disconnectedByCaller || reconnectTimer !== null) {
      return;
    }

    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempts,
      RECONNECT_MAX_DELAY_MS,
    );
    reconnectAttempts += 1;

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      openSocket();
    }, delay);
  }

  function openSocket(): void {
    if (socket) {
      return;
    }

    setState('connecting');
    const ws = new WebSocket(config.url);
    socket = ws;

    ws.addEventListener('open', () => {
      reconnectAttempts = 0;
      setState('open');
    });

    ws.addEventListener('message', (event) => {
      let payload: unknown;

      try {
        payload = JSON.parse(String(event.data));
      } catch {
        // Not valid JSON — nothing meaningful to forward, and this SDK
        // never guesses at what an unparseable message might mean.
        return;
      }

      for (const handler of messageHandlers) {
        handler(payload);
      }
    });

    ws.addEventListener('close', () => {
      socket = null;
      setState('closed');

      if (!disconnectedByCaller) {
        scheduleReconnect();
      }
    });
  }

  return {
    connect(): void {
      disconnectedByCaller = false;
      reconnectAttempts = 0;
      openSocket();
    },

    disconnect(): void {
      disconnectedByCaller = true;
      clearReconnectTimer();
      socket?.close();
      socket = null;
      setState('closed');
    },

    send(payload: unknown): void {
      if (!socket || state !== 'open') {
        throw new ChatWebSocketError('Cannot send a message: the connection is not open.');
      }

      socket.send(JSON.stringify(payload));
    },

    getState(): ConnectionState {
      return state;
    },

    onMessage(handler: (payload: unknown) => void): void {
      messageHandlers.add(handler);
    },

    offMessage(handler: (payload: unknown) => void): void {
      messageHandlers.delete(handler);
    },

    onStateChange(handler: (state: ConnectionState) => void): void {
      stateHandlers.add(handler);
    },

    offStateChange(handler: (state: ConnectionState) => void): void {
      stateHandlers.delete(handler);
    },
  };
}
